import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { FiCheckCircle, FiAlertCircle, FiInfo } from 'react-icons/fi';

const statusConfig = {
  success: {
    icon: <FiCheckCircle className="w-12 h-12 text-green-500" />,
    inlineIcon: <FiCheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />,
    title: 'Successfully Unsubscribed',
    subtitle: 'Your preference has been updated',
    message: 'You have been successfully unsubscribed from our newsletter. You will no longer receive emails from us.',
    blockBg: 'bg-green-50',
    blockBorder: 'border-green-200',
    titleText: 'text-green-800',
    bodyText: 'text-green-700',
  },
  already: {
    icon: <FiInfo className="w-12 h-12 text-blue-500" />,
    inlineIcon: <FiInfo className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />,
    title: 'Already Unsubscribed',
    subtitle: 'No changes were needed',
    message: 'This email address is already unsubscribed from our newsletter.',
    blockBg: 'bg-blue-50',
    blockBorder: 'border-blue-200',
    titleText: 'text-blue-800',
    bodyText: 'text-blue-700',
  },
  invalid: {
    icon: <FiAlertCircle className="w-12 h-12 text-red-500" />,
    inlineIcon: <FiAlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />,
    title: 'Invalid Link',
    subtitle: 'This link could not be processed',
    message: 'This unsubscribe link is invalid or has already been used. If you continue to receive emails, please contact us.',
    blockBg: 'bg-yellow-50',
    blockBorder: 'border-yellow-200',
    titleText: 'text-yellow-800',
    bodyText: 'text-yellow-700',
  },
};

const NewsletterUnsubscribe = () => {
  const [searchParams] = useSearchParams();
  const [config, setConfig] = useState(null);

  useEffect(() => {
    const status = searchParams.get('status');
    setConfig(statusConfig[status] || statusConfig['invalid']);
  }, [searchParams]);

  if (!config) return null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">

          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              {config.icon}
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              {config.title}
            </h2>
            <p className="text-gray-600">
              {config.subtitle}
            </p>
          </div>

          {/* Status notice block — mirrors Login's verification notice */}
          <div className={`mb-6 p-4 ${config.blockBg} border ${config.blockBorder} rounded-lg`}>
            <div className="flex items-start">
              {config.inlineIcon}
              <div className="ml-3 flex-1">
                <p className={`text-sm font-medium ${config.titleText}`}>
                  {config.title}
                </p>
                <p className={`text-sm mt-1 ${config.bodyText}`}>
                  {config.message}
                </p>
              </div>
            </div>
          </div>

          {/* Divider — same pattern as Login */}
          <div className="mb-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">
                  What would you like to do next?
                </span>
              </div>
            </div>
          </div>

          {/* Primary CTA — same style as Login's submit button */}
          <div className="space-y-3">
            <Link
              to="/"
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-300"
            >
              Back to Home
            </Link>
          </div>

          {/* Footer link — mirrors Login's "Don't have an account?" section */}
          <div className="text-center pt-6 border-t border-gray-200 mt-8">
            <p className="text-sm text-gray-600">
              Changed your mind?{' '}
              <Link
                to="/#newsletter"
                className="font-medium text-green-600 hover:text-green-700 transition-colors"
              >
                Resubscribe here
              </Link>
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default NewsletterUnsubscribe;